import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Menu, X, User, LogIn, ChevronDown, ShoppingCart } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useSetting } from "@/context/CmsContext";
import { useLocale } from "@/context/LocaleContext";
import { useCart } from "@/context/CartContext";
import CartDrawer from "./CartDrawer";
import Logo from "./Logo";

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [modelosOpen, setModelosOpen] = useState(false);
    const [mobileModelosOpen, setMobileModelosOpen] = useState(false);
    const closeTimer = useRef(null);
    const { pathname } = useLocation();
    const { user } = useAuth();
    const { tr, lang } = useLocale();
    const { count, setOpen: setCartOpen } = useCart();
    const ctaLabel = useSetting("header_cta_label", tr("Peça o seu", "Get yours"), { localized: lang === "en" });

    const MODELOS = [
        { to: "/modelo-alfa", label: tr("Modelo Alfa", "Alfa Model"), description: tr("Compacto · single-product", "Compact · single-product") },
        { to: "/modelo-omega", label: tr("Modelo Ômega", "Omega Model"), description: tr("Premium · maior volume", "Premium · higher volume") },
    ];

    const NAV = [
        { to: "/", label: "Home" },
        { type: "dropdown", label: tr("Modelos", "Models"), items: MODELOS, matchPaths: ["/modelo-alfa", "/modelo-omega"] },
        { to: "/processos", label: tr("Processos", "Process") },
        { to: "/galeria", label: tr("Galeria", "Gallery") },
        { to: "/simulador", label: tr("Simulador", "Configurator") },
        { to: "/loja", label: tr("Loja", "Shop") },
        { to: "/contacto", label: tr("Contacto", "Contact") },
    ];

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        setModelosOpen(false);
        setMobileModelosOpen(false);
    }, [pathname]);

    const accountTo = user?.role === "admin" ? "/admin" : "/conta";
    const accountLabel = user ? tr("A minha conta", "My account") : tr("Entrar", "Sign in");
    const AccountIcon = user ? User : LogIn;

    const openDropdown = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        setModelosOpen(true);
    };
    const scheduleCloseDropdown = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setModelosOpen(false), 120);
    };

    return (
        <header
            data-testid="site-header"
            className={cn(
                "sticky top-0 z-50 w-full transition-all",
                scrolled ? "glass-header shadow-sm" : "bg-background/70 backdrop-blur-md border-b border-transparent"
            )}
        >
            <div className="container-x flex h-16 items-center justify-between">
                <Logo testId="header-logo" size="md" />

                <nav className="hidden lg:flex items-center gap-8" data-testid="primary-nav">
                    {NAV.map((item) => {
                        if (item.type === "dropdown") {
                            const isActive = item.matchPaths?.some((p) => pathname.startsWith(p));
                            return (
                                <div
                                    key={item.label}
                                    className="relative"
                                    onMouseEnter={openDropdown}
                                    onMouseLeave={scheduleCloseDropdown}
                                >
                                    <button
                                        type="button"
                                        data-testid="nav-link-modelos"
                                        onClick={() => setModelosOpen((v) => !v)}
                                        aria-haspopup="menu"
                                        aria-expanded={modelosOpen}
                                        className={cn(
                                            "relative inline-flex items-center gap-1 text-sm font-medium text-primary/80 link-underline transition-colors hover:text-primary",
                                            isActive && "text-primary font-semibold"
                                        )}
                                    >
                                        {item.label}
                                        <ChevronDown
                                            className={cn(
                                                "h-3.5 w-3.5 transition-transform",
                                                modelosOpen && "rotate-180"
                                            )}
                                        />
                                    </button>
                                    {modelosOpen && (
                                        <div
                                            data-testid="modelos-dropdown"
                                            role="menu"
                                            onMouseEnter={openDropdown}
                                            onMouseLeave={scheduleCloseDropdown}
                                            className="absolute left-1/2 top-full -translate-x-1/2 pt-3"
                                        >
                                            <div className="w-72 rounded-xl border border-border bg-white shadow-xl ring-1 ring-black/5 overflow-hidden">
                                                {item.items.map((sub) => (
                                                    <NavLink
                                                        key={sub.to}
                                                        to={sub.to}
                                                        role="menuitem"
                                                        data-testid={`nav-dropdown-${sub.label.toLowerCase().replace(/\s+/g, "-")}`}
                                                        className={({ isActive: subActive }) =>
                                                            cn(
                                                                "flex flex-col gap-0.5 px-4 py-3 text-sm transition hover:bg-muted",
                                                                subActive
                                                                    ? "bg-muted text-primary font-semibold"
                                                                    : "text-primary/85"
                                                            )
                                                        }
                                                    >
                                                        <span className="font-semibold">{sub.label}</span>
                                                        <span className="text-xs text-muted-foreground">{sub.description}</span>
                                                    </NavLink>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        }
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/"}
                                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    cn(
                                        "relative text-sm font-medium text-primary/80 link-underline transition-colors hover:text-primary",
                                        isActive && "text-primary font-semibold"
                                    )
                                }
                            >
                                {item.label}
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setCartOpen(true)}
                        data-testid="cart-button"
                        aria-label="Carrinho"
                        className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-primary/15 bg-white/60 text-primary backdrop-blur transition hover:bg-white hover:-translate-y-0.5"
                    >
                        <ShoppingCart className="h-4 w-4" />
                        {count > 0 && (
                            <span data-testid="cart-count" className="absolute -top-1 -right-1 h-5 min-w-[1.25rem] rounded-full bg-secondary px-1 text-[10px] font-bold text-secondary-foreground flex items-center justify-center">{count}</span>
                        )}
                    </button>
                    <Link
                        to={user ? accountTo : "/entrar"}
                        data-testid="header-cta-account"
                        className="inline-flex items-center gap-1.5 rounded-full border border-primary/15 bg-white/60 px-4 py-2 text-sm font-bold text-primary backdrop-blur transition hover:bg-white hover:-translate-y-0.5"
                    >
                        <AccountIcon className="h-3.5 w-3.5" /> {accountLabel}
                    </Link>
                    <Link
                        to="/simulador"
                        data-testid="header-cta-contacto"
                        className="inline-flex items-center rounded-full bg-secondary px-5 py-2 text-sm font-bold text-secondary-foreground shadow-[0_8px_24px_-8px_rgba(224,174,50,0.5)] transition hover:-translate-y-0.5 hover:shadow-[0_14px_32px_-10px_rgba(224,174,50,0.6)]"
                    >
                        {ctaLabel}
                    </Link>
                </div>

                <button
                    type="button"
                    data-testid="mobile-cart-button"
                    onClick={() => setCartOpen(true)}
                    className="lg:hidden relative inline-flex h-10 w-10 items-center justify-center rounded-md border border-border bg-white text-primary mr-2"
                    aria-label="Carrinho"
                >
                    <ShoppingCart className="h-5 w-5" />
                    {count > 0 && (
                        <span className="absolute -top-1 -right-1 h-5 min-w-[1.25rem] rounded-full bg-secondary px-1 text-[10px] font-bold text-secondary-foreground flex items-center justify-center">{count}</span>
                    )}
                </button>
                <button
                    type="button"
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-md border border-border bg-white text-primary"
                    aria-label="Menu"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-border bg-white" data-testid="mobile-nav">
                    <div className="container-x py-4 flex flex-col gap-1">
                        {NAV.map((item) => {
                            if (item.type === "dropdown") {
                                const isActive = item.matchPaths?.some((p) => pathname.startsWith(p));
                                return (
                                    <div key={item.label} className="flex flex-col">
                                        <button
                                            type="button"
                                            data-testid="mobile-nav-toggle-modelos"
                                            onClick={() => setMobileModelosOpen((v) => !v)}
                                            className={cn(
                                                "flex items-center justify-between rounded-md px-3 py-2.5 text-sm font-medium text-primary/80 hover:bg-muted",
                                                isActive && "bg-muted text-primary font-semibold"
                                            )}
                                        >
                                            <span>{item.label}</span>
                                            <ChevronDown
                                                className={cn(
                                                    "h-4 w-4 transition-transform",
                                                    mobileModelosOpen && "rotate-180"
                                                )}
                                            />
                                        </button>
                                        {mobileModelosOpen && (
                                            <div className="ml-3 mt-1 flex flex-col gap-1 border-l border-border pl-3">
                                                {item.items.map((sub) => (
                                                    <NavLink
                                                        key={sub.to}
                                                        to={sub.to}
                                                        data-testid={`mobile-nav-dropdown-${sub.label.toLowerCase().replace(/\s+/g, "-")}`}
                                                        className={({ isActive: subActive }) =>
                                                            cn(
                                                                "rounded-md px-3 py-2 text-sm font-medium text-primary/80 hover:bg-muted",
                                                                subActive && "bg-muted text-primary font-semibold"
                                                            )
                                                        }
                                                    >
                                                        {sub.label}
                                                    </NavLink>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            }
                            return (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    end={item.to === "/"}
                                    data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={({ isActive }) =>
                                        cn(
                                            "rounded-md px-3 py-2.5 text-sm font-medium text-primary/80 hover:bg-muted",
                                            isActive && "bg-muted text-primary font-semibold"
                                        )
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            );
                        })}
                        <Link
                            to={user ? accountTo : "/entrar"}
                            data-testid="mobile-cta-account"
                            className="mt-2 inline-flex items-center justify-center gap-2 rounded-md border border-primary/20 bg-white px-4 py-2.5 text-sm font-bold text-primary"
                        >
                            <AccountIcon className="h-4 w-4" /> {accountLabel}
                        </Link>
                        <Link
                            to="/simulador"
                            className="mt-2 inline-flex items-center justify-center rounded-md bg-secondary px-4 py-2.5 text-sm font-bold text-secondary-foreground"
                        >
                            {ctaLabel}
                        </Link>
                    </div>
                </div>
            )}
            <CartDrawer />
        </header>
    );
}
